/* Customer portal — Delivery Dockets: list, download PDF, approve. */
import { useEffect, useState } from "react";
import { api, API_BASE, apiHref } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";

export default function PortalDockets() {
  const [dockets, setDockets] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/portal/dockets");
      setDockets(r.data);
    } catch (e) { console.warn("portal dockets load failed", e); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/portal/dockets/${id}/approve`);
      toast.success("Docket approved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approve failed");
    } finally { setBusyId(null); }
  };

  const openPdf = (id) => {
    window.open(apiHref(`/portal/dockets/${id}/pdf`), "_blank");
  };

  const pending = dockets.filter((d) => d.status === "delivered" && !d.customer_approved);
  const approved = dockets.filter((d) => d.customer_approved);
  const inflight = dockets.filter((d) => d.status !== "delivered");

  return (
    <div className="space-y-8" data-testid="portal-dockets">
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-neutral-500">Dockets</div>
        <h1 className="text-4xl font-black">Delivery dockets</h1>
        <p className="text-neutral-500 text-sm mt-1">Approve completed loads and download PDFs for your records.</p>
      </div>

      {pending.length > 0 && <Section title="Awaiting your approval" tone="warn" list={pending} onApprove={approve} onPdf={openPdf} busyId={busyId} />}
      {inflight.length > 0 && <Section title="In progress" tone="info" list={inflight} onPdf={openPdf} />}
      {approved.length > 0 && <Section title="Approved & filed" tone="good" list={approved} onPdf={openPdf} />}
      {dockets.length === 0 && (
        <div className="bg-white border border-neutral-200 p-10 text-center text-neutral-500">No dockets yet.</div>
      )}
    </div>
  );
}

function Section({ title, tone, list, onApprove, onPdf, busyId }) {
  const toneMap = {
    warn: "text-amber-600",
    info: "text-neutral-600",
    good: "text-emerald-600",
  };
  return (
    <div>
      <h2 className={`text-[11px] uppercase tracking-widest mb-2 font-bold ${toneMap[tone]}`}>{title} · {list.length}</h2>
      <div className="bg-white border border-neutral-200 divide-y divide-neutral-100">
        {list.map((d) => (
          <div key={d.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`portal-docket-${d.id}`}>
            <div className="w-10 h-10 bg-primary-orange/10 text-primary-orange flex items-center justify-center rounded shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">
                {d.docket_no}
                <span className="ml-2 mono text-neutral-500 text-xs uppercase">{d.mix_code}</span>
              </div>
              <div className="text-xs text-neutral-500 truncate">{d.site_address}</div>
              <div className="text-[11px] text-neutral-500 mono mt-0.5">
                {d.truck_number ? `Truck ${d.truck_number}` : ""}{d.driver_name ? ` · ${d.driver_name}` : ""}
              </div>
            </div>
            <div className="text-right">
              <div className="mono font-bold text-sm">{d.quantity_m3} m³</div>
              <div className="mono text-[11px] text-neutral-500">${(d.total || 0).toFixed(2)}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button data-testid={`docket-pdf-${d.id}`} variant="outline" onClick={() => onPdf(d.id)} className="rounded-none border-neutral-300 uppercase tracking-widest text-xs">
                <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
              </Button>
              {onApprove && (
                <Button
                  data-testid={`docket-approve-${d.id}`}
                  onClick={() => onApprove(d.id)}
                  disabled={busyId === d.id}
                  className="rounded-none bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Approve
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
